import {Component, LogTag, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Mathf} from '../utils/Mathf.js';
import {PuzzleCamera} from './puzzle-camera.js';
import {quat, vec3} from 'gl-matrix';
import {BoardColor, BoardObject} from './board-object.js';
import {CustomLogTag} from '../classes/Constants.js';
import {EventEmitter} from '../utils/EventEmitter.js';
import {ScoreController, ScoreType} from './score-controller.js';
import {GameRules} from './rules/game-rules.js';
import {FallingObjectsSpawner} from './falling-objects-spawner.js';
import {Pair} from '../classes/Pair.js';
import {PopData} from '../classes/PopData.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {FailStateNames} from '../enums/FailStateNames.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {RNG} from '../utils/rng.js';
import {SpecialBombType} from '../enums/SpecialBombType.js';
import {Statistics} from './statistics.js';
import {StatNames} from '../enums/StatNames.js';
import {Analytics, AnalyticsEvent} from '../classes/Analytics.js';
import {TimeManager} from './time-manager.js';
import {Tags} from './tags.js';

export const PuzzleConstants = {
    columnCount: 15,
    rowCount: 13,
    columnRadius: 2.5,
    localColumnRotation: 180,
    rowSize: 1,
};

const tempVec = vec3.create();
const tempQuat = quat.create();

export class Puzzle extends Component {
    static TypeName = 'puzzle';

    @property.object({required: true})
    mainCameraObject!: Object3D;

    @property.object({required: true})
    scoreControllerObject!: Object3D;

    @property.object({required: true})
    nextFruitSlot!: Object3D;

    @property.float(3)
    delayBeforeGameStart = 3; // in seconds

    @property.float(1.5)
    delayBeforeGameEnd = 1.5; // in seconds

    @property.float(1)
    delayForVictoryExplosion = 1; // in seconds

    @property.float(0)
    explodeAllOfColorDelay = 0;

    @property.float(0.17)
    explosionStepDelay = 0.17;

    @property.float(0.5)
    boardShiftDelay = 0.5;

    moveUpSoon: boolean = false;
    gameInProgress = false;
    dropSpeedFactor = 1.0;

    // Singleton
    private static _puzzleInstance: Puzzle;
    static get instance(): Puzzle {
        return Puzzle._puzzleInstance;
    }

    private _mainCamera!: PuzzleCamera;
    get mainCamera(): PuzzleCamera {
        return this._mainCamera;
    }

    private _score: ScoreController;
    get score() {
        return this._score;
    }

    private _lanes: Object3D[] = [];
    get lanes(): Object3D[] {
        return this._lanes;
    }

    private _rules: GameRules;
    set rules(value: GameRules) {
        this._rules = value;
        if (this._rules) {
            this._rules.puzzleController = this;
        }
    }
    get rules() {
        return this._rules;
    }

    private _spawner: FallingObjectsSpawner;
    set spawner(value: FallingObjectsSpawner) {
        this._spawner = value;
        if (this._spawner) {
            this._spawner.setup(this);
        }
    }
    get spawner() {
        return this._spawner;
    }

    get height(): number {
        return PuzzleConstants.rowSize * (PuzzleConstants.rowCount - 1);
    }

    private _level = 1;
    get level() {
        return this._level;
    }
    set level(value: number) {
        this._level = value;
        this.score.level = value;
    }
    private _randomVarianceFactor = 0.0;
    get randomVariance(): number {
        return this._randomVarianceFactor;
    }

    private _resetGameDelegates: EventEmitter<Puzzle> = new EventEmitter<Puzzle>();
    private _turnCompleteDelegates: EventEmitter<Puzzle> = new EventEmitter<Puzzle>();
    private _newGameDelegates: EventEmitter<Puzzle> = new EventEmitter<Puzzle>();
    private _gameOverDelegates: EventEmitter<Puzzle> = new EventEmitter<Puzzle>();
    private _stackChangeDelegates: EventEmitter<Puzzle> = new EventEmitter<Puzzle>();

    private _board: BoardObject[];
    private _bottomRow = 0;
    private _gameOver = false;

    private _spawnNextPieceDelay = 0.0;
    private _fallingObjects: number = 0;
    private _dirty: boolean;
    private _turnAdvance: boolean;
    private _puzzleState = -1;
    private _pendingActivations: Array<Pair<BoardObject, Pair<number, number>>> = new Array<
        Pair<BoardObject, Pair<number, number>>
    >();
    private _pendingChains: Array<Pair<BoardObject, Pair<number, number>>> = new Array<
        Pair<BoardObject, Pair<number, number>>
    >();
    private _pendingRemovals: Array<Pair<BoardObject, Pair<number, number>>> = new Array<
        Pair<BoardObject, Pair<number, number>>
    >();
    private _objectsToDestroy: Map<number, BoardObject> = new Map<number, BoardObject>();
    private _pendingPops: PopData[] = new Array<PopData>();
    private _time: number = 0;
    private _gameStartTime: number;

    private _coroutineManager: CoroutineManager = new CoroutineManager();
    private _scoreController: ScoreController;

    init() {
        if (Puzzle._puzzleInstance) {
            this.engine.log.error(
                CustomLogTag.Game,
                'There can only be one instance of Puzzle Component'
            );
        }
        Puzzle._puzzleInstance = this;
    }

    start() {
        this._scoreController = ScoreController.instance;

        this._mainCamera = this.mainCameraObject.getComponent(PuzzleCamera);
        this._score = this.scoreControllerObject.getComponent(ScoreController);

        this._generateColumns();

        // create an empty board
        this._board = new Array(
            PuzzleConstants.columnCount * PuzzleConstants.rowCount
        ).fill(undefined);

        this._bottomRow = PuzzleConstants.rowCount - 1;
        this.engine.log.info(LogTag.Component, 'Puzzle initialized');
    }

    update(dt: number) {
        if (TimeManager.instance.paused) {
            // bit of a hacky way to pause the game
            return;
        }
        this._time += dt;
        this._coroutineManager.update(dt);
    }

    private _runGameId = -1;
    private *_runGame() {
        this.gameInProgress = false;

        // wait one frame for everything else to start.
        yield null;

        this._destroyBoard();

        this._scoreController.resetScore();
        this._scoreController.resetTime();

        let gameOver = false;
        this.moveUpSoon = false;
        this.dropSpeedFactor = 1.0;
        this.level = 1;
        this._spawnNextPieceDelay = 0.0;

        this._randomVarianceFactor = Math.random();

        // We're starting a new game now.
        this._newGameDelegates.emit(this);

        yield null;

        this.spawner.startGame();
        this.rules.startNewGame();

        // opening UI
        this._scoreController.startNewGame();

        this._moveBoardToPosition();

        this._stackChangeDelegates.emit(this);

        this.gameInProgress = true;

        yield* waitForSeconds(this.delayBeforeGameStart);

        let gameStartTime = this._time;

        while (!gameOver) {
            // new line might cause combinations.
            let dirty = false;
            dirty = this._findCombinations();
            // no chains possible at this point.  Three sets might occur on land, though.
            if (dirty) {
                this._fallBack();
            }

            do {
                if (this._spawnNextPieceDelay > 0.0) {
                    let timer = this._time + this._spawnNextPieceDelay;
                    while (this._time < timer) {
                        if (this._findChains()) {
                            this._fallBack();
                        }

                        yield null;
                    }
                }

                while (this._fallingObjects > 0) {
                    dirty = false;
                    dirty = this._findCombinations();
                    dirty = this._findChains() || dirty;
                    dirty = this._findThreeSets() || dirty;
                    if (dirty) {
                        this._fallBack();
                    }

                    // wait for everything to land before firing off another fruit.
                    yield null;
                }

                yield null;

                this._turnAdvance = !this.spawner.spawn();

                do {
                    yield null;

                    dirty = false;
                    dirty = this._findCombinations();
                    dirty = this._findChains() || dirty;
                    dirty = this._findThreeSets() || dirty;
                    if (dirty) {
                        this._fallBack();
                    }
                } while (!this._turnAdvance);

                this._turnCompleteDelegates.emit(this);

                this.rules.turnComplete();

                // Turn complete callbacks may have registered blobs for popping,
                // chaining, or removal.  Check before we look at game over.
                if (this._findChains()) {
                    this._fallBack();
                }

                if (this.rules.warnStackChange()) {
                    this.moveUpSoon = true;
                }

                gameOver = this.rules.detectGameOver();

                while (
                    this._fallingObjects > 0 &&
                    !gameOver &&
                    this.rules.timeToChangeStack()
                ) {
                    // if it's time to rise, wait until everything has landed
                    yield null;
                    gameOver = this.rules.detectGameOver();
                }
            } while (
                !gameOver &&
                (this._fallingObjects > 0 || !this.rules.timeToChangeStack())
            );

            //	Logger.Log("move up soon = false");

            this.moveUpSoon = false;

            // if top row is full then game over
            // else move everything up
            if (!gameOver) {
                if (this.rules.changeStack()) {
                    this._moveBoardToPosition();

                    this._stackChangeDelegates.emit(this);

                    yield null;

                    let timer = this._time + this.boardShiftDelay;
                    while (this._time < timer) {
                        if (this._findChains()) {
                            this._fallBack();
                        }

                        yield null;
                    }

                    yield null;

                    gameOver = this.rules.detectGameOver();
                }
            }
        }

        //Logger.Log("get the fail state " + Time.time);

        // call Game Rules instead; game rules will pass to score controller the enum
        // const failState = this.rules.failStateCause;

        // this._scoreController.gameOver(failState);

        // this._scoreController.hideHUD();

        // // This starts Mal's game over animation, which eventually will trigger the
        // // transition to the next UI screen.
        // this.spawner.gameOver(failState);

        // let gameDuration = this._time - gameStartTime;
        // Statistics.instance.recordAdditive(
        //     StatNames.TotalPlayTime,
        //     Math.floor(gameDuration)
        // );

        // if (failState == FailStateNames.MaxLevelReached) {
        //     //add this here
        //     this._endGame();

        //     let highRow = -1;
        //     for (let col = 0; col < PuzzleConstants.columnCount; col++) {
        //         highRow = Math.max(highRow, this.getHighestOccupiedRow(col));
        //     }
        //     let step = 0;
        //     const delay = this.delayForVictoryExplosion / (highRow + 1);
        //     while (highRow >= 0) {
        //         for (let col = 0; col < PuzzleConstants.columnCount; col++) {
        //             let index = this._cellToBoardIndex(highRow, col);
        //             if (this._board[index]) {
        //                 let blobDelay = RNG.getUniform(0.0, delay * 5.0);
        //                 this._board[index].pop(blobDelay, step, false);
        //                 this._board[index] = null;
        //             }
        //         }
        //         step++;
        //         highRow--;
        //         yield* waitForSeconds(delay);
        //     }
        // }
        // // EndGame() must be called before rules.GameOver() so that GameOver callbacks
        // // have the chance to modify score before the final results are tallied.
        // else {
        //     this._endGame();
        // }

        // yield* waitForSeconds(this.delayBeforeGameEnd);

        //Logger.Log("rules game over " + Time.time);
        //this.rules.gameOver(failState);

        // just in case, stop the coroutine.
        yield* this._gameOverSequence();
    }

    private _gameOverSequenceId = -1;
    private *_gameOverSequence() {
        // call Game Rules instead; game rules will pass to score controller the enum
        const failState = this.rules.failStateCause;

        this._scoreController.gameOver(failState);

        this._scoreController.hideHUD();

        // This starts Mal's game over animation, which eventually will trigger the
        // transition to the next UI screen.
        this._spawner.gameOver(failState);

        const gameDuration = this._time - this._gameStartTime;
        Statistics.instance.recordAdditive(
            StatNames.TotalPlayTime,
            Math.floor(gameDuration)
        );

        if (failState == FailStateNames.MaxLevelReached) {
            //add this here
            this._endGame();

            let highRow = -1;
            for (let col = 0; col < PuzzleConstants.columnCount; col++) {
                highRow = Math.max(highRow, this.getHighestOccupiedRow(col));
            }
            let step = 0;
            const delay = this.delayForVictoryExplosion / (highRow + 1);
            while (highRow >= 0) {
                for (let col = 0; col < PuzzleConstants.columnCount; col++) {
                    let index = this._cellToBoardIndex(highRow, col);
                    if (this._board[index]) {
                        const blobDelay = RNG.getUniform(0.0, delay * 5.0);
                        this._board[index].pop(blobDelay, step, false);
                        this._board[index] = null;
                    }
                }
                step++;
                highRow--;
                yield* waitForSeconds(delay);
            }
        }
        // EndGame() must be called before rules.GameOver() so that GameOver callbacks
        // have the chance to modify score before the final results are tallied.
        else {
            this._endGame();
        }

        yield* waitForSeconds(this.delayBeforeGameEnd);

        this.rules.gameOver(failState);
    }

    poppedBlob(color: BoardColor) {
        this.rules.poppedBlob(color);
    }

    stackFull(): boolean {
        let stackFull = false;
        for (let x = 0; x < PuzzleConstants.columnCount; x++) {
            // if there's anything in the top row then the stack is full.
            if (this._board[x] != null) {
                stackFull = true;
                break;
            }
        }

        return stackFull;
    }

    private _findChains(): boolean {
        let dirty = false;
        const activationsCount = this._pendingActivations.length;
        if (activationsCount > 0) {
            for (let x = activationsCount - 1; x >= 0; x--) {
                const location = this._pendingActivations[x];
                const pendingComboRow = location.Second.First;
                const pendingComboColumn = location.Second.Second;
                this._pendingActivations.splice(x, 1);

                const index = this._cellToBoardIndex(pendingComboRow, pendingComboColumn);

                const obj = this._board[index];
                if (obj) {
                    if (location.First != this._board[index]) {
                        this.engine.log.info(
                            CustomLogTag.Game,
                            'Activating different object than we registered (' +
                                pendingComboRow +
                                ', ' +
                                pendingComboColumn +
                                ') camera is ' +
                                this._mainCamera.findClosestLane() +
                                '.'
                        );
                    } else {
                        obj.activateSpecial();
                        dirty = true;
                    }
                } else {
                    this.engine.log.info(
                        CustomLogTag.Game,
                        'Not activating NULL object at (' +
                            pendingComboRow +
                            ', ' +
                            pendingComboColumn +
                            ') camera is ' +
                            this._mainCamera.findClosestLane() +
                            '.'
                    );
                }
            }
        }

        const chainsCount = this._pendingChains.length;
        if (chainsCount > 0) {
            dirty = true;
            for (let x = 0; x < chainsCount; x++) {
                const location = this._pendingChains[x];
                const pendingComboRow = location.Second.First;
                const pendingComboColumn = location.Second.Second;
                const index = this._cellToBoardIndex(pendingComboRow, pendingComboColumn);
                if (this._board[index]) {
                    if (location.First != this._board[index]) {
                        this.engine.log.info(
                            CustomLogTag.Game,
                            'Chaining different object than we registered (' +
                                pendingComboRow +
                                ', ' +
                                pendingComboColumn +
                                ') camera is ' +
                                this._mainCamera.findClosestLane() +
                                '.'
                        );
                    } else {
                        this._startChain(pendingComboRow, pendingComboColumn);
                    }
                } else {
                    this.engine.log.info(
                        CustomLogTag.Game,
                        'Chaining NULL object (' +
                            pendingComboRow +
                            ', ' +
                            pendingComboColumn +
                            ') camera is ' +
                            this._mainCamera.findClosestLane() +
                            '.'
                    );
                }
            }
            this._pendingChains.length = 0; // clear the list
        }

        const popsCount = this._pendingPops.length;
        if (popsCount > 0) {
            for (let x = popsCount - 1; x >= 0; x--) {
                const location = this._pendingPops[x];
                const pendingComboRow = location.row;
                const pendingComboColumn = location.col;
                this._pendingPops.splice(x, 1);

                const index = this._cellToBoardIndex(pendingComboRow, pendingComboColumn);

                const obj = this._board[index];
                if (obj) {
                    if (location.obj != this._board[index]) {
                        this.engine.log.info(
                            CustomLogTag.Game,
                            'Popping different object than we registered (' +
                                pendingComboRow +
                                ', ' +
                                pendingComboColumn +
                                ') camera is ' +
                                this.mainCamera.findClosestLane() +
                                '.'
                        );
                    } else {
                        this._board[index] = null;
                        obj.pop(location.delay, location.distance, false);
                        dirty = true;
                    }
                } else {
                    this.engine.log.info(
                        CustomLogTag.Game,
                        'Popping NULL object (' +
                            pendingComboRow +
                            ', ' +
                            pendingComboColumn +
                            ') camera is ' +
                            this.mainCamera.findClosestLane() +
                            '.'
                    );
                }
            }
        }

        const removalsCount = this._pendingRemovals.length;
        if (removalsCount > 0) {
            for (let x = removalsCount - 1; x >= 0; x--) {
                const location = this._pendingRemovals[x];
                const pendingComboRow = location.Second.First;
                const pendingComboColumn = location.Second.Second;
                this._pendingRemovals.splice(x, 1);

                const index = this._cellToBoardIndex(pendingComboRow, pendingComboColumn);

                const obj = this._board[index];
                if (obj) {
                    if (location.First != this._board[index]) {
                        this.engine.log.info(
                            CustomLogTag.Game,
                            'Removing different object than we registered (' +
                                pendingComboRow +
                                ', ' +
                                pendingComboColumn +
                                ') camera is ' +
                                this._mainCamera.findClosestLane() +
                                '.'
                        );
                    } else {
                        this._board[index] = null;
                        // should the puzzle really destroy objects here?
                        obj.object.destroy();
                        dirty = true;
                    }
                } else {
                    this.engine.log.info(
                        CustomLogTag.Game,
                        'Removing NULL object (' +
                            pendingComboRow +
                            ', ' +
                            pendingComboColumn +
                            ') camera is ' +
                            this.mainCamera.findClosestLane() +
                            '.'
                    );
                }
            }
        }
        return dirty;
    }

    private _findThreeSets(): boolean {
        let dirty = false;
        // we're looking for fruit in two cases:
        // 0 0 0    0
        //			0
        //			0

        let fruitCount = 0;
        for (let row = 0; row < PuzzleConstants.rowCount; row++) {
            for (let col = 0; col < PuzzleConstants.columnCount; col++) {
                const index = this._cellToBoardIndex(row, col);
                const obj = this._board[index];

                if (obj && this._isFruit(obj)) {
                    // scan right
                    const indexRight = this._cellToBoardIndex(
                        row,
                        Mathf.repeat(col + 1, PuzzleConstants.columnCount)
                    );
                    const indexTwoRight = this._cellToBoardIndex(
                        row,
                        Mathf.repeat(col + 2, PuzzleConstants.columnCount)
                    );
                    const rightObj = this._board[indexRight];
                    const twoRightObj = this._board[indexTwoRight];

                    if (
                        rightObj &&
                        this._isFruit(rightObj) &&
                        this._exactColorMatch(obj, rightObj) &&
                        twoRightObj &&
                        this._isFruit(twoRightObj) &&
                        this._exactColorMatch(obj, twoRightObj)
                    ) {
                        let fruitSoFar = fruitCount;
                        // three fruit side-by-side
                        if (!this._objectsToDestroy.has(index)) {
                            this._objectsToDestroy.set(index, obj);
                            fruitCount++;
                        }

                        if (!this._objectsToDestroy.has(indexRight)) {
                            this._objectsToDestroy.set(indexRight, rightObj);
                            fruitCount++;
                        }

                        if (!this._objectsToDestroy.has(indexTwoRight)) {
                            this._objectsToDestroy.set(indexTwoRight, twoRightObj);
                            fruitCount++;
                        }

                        // Was this match a rainbow fruit?
                        if (obj.color == BoardColor.Any && fruitCount - fruitSoFar == 3) {
                            Statistics.instance.recordAdditive(
                                StatNames.RainbowFruitMatches,
                                1
                            );
                        }
                    }

                    if (row > 1) {
                        const indexBelow = this._cellToBoardIndex(row - 1, col);
                        const indexTwoBelow = this._cellToBoardIndex(row - 2, col);

                        const belowObj = this._board[indexBelow];
                        const twoBelowObj = this._board[indexTwoBelow];

                        if (
                            belowObj &&
                            this._isFruit(belowObj) &&
                            this._exactColorMatch(obj, belowObj) &&
                            twoBelowObj &&
                            this._isFruit(twoBelowObj) &&
                            this._exactColorMatch(obj, twoBelowObj)
                        ) {
                            let fruitSoFar = fruitCount;
                            // three fruit of the same color stacked on top of each other.
                            if (!this._objectsToDestroy.has(index)) {
                                this._objectsToDestroy.set(index, obj);
                                fruitCount++;
                            }

                            if (!this._objectsToDestroy.has(indexBelow)) {
                                this._objectsToDestroy.set(indexBelow, belowObj);
                                fruitCount++;
                            }

                            if (!this._objectsToDestroy.has(indexTwoBelow)) {
                                this._objectsToDestroy.set(indexTwoBelow, twoBelowObj);
                                fruitCount++;
                            }

                            // Was this match a rainbow fruit?
                            if (
                                obj.color == BoardColor.Any &&
                                fruitCount - fruitSoFar == 3
                            ) {
                                Statistics.instance.recordAdditive(
                                    StatNames.RainbowFruitMatches,
                                    1
                                );
                            }
                        }
                    }
                }
            }
        }

        // first update the board
        for (const entry of this._objectsToDestroy) {
            this._board[entry[0]] = null;
        }

        let firstFruit: Object3D = null;
        // then inform the objects of their demise
        for (const entry of this._objectsToDestroy) {
            if (firstFruit == null) {
                firstFruit = entry[1].object;
            }

            entry[1].pop(0, 0, false);
        }

        if (firstFruit != null && this._objectsToDestroy.size > 0) {
            const newScore = this._rules.calculateScore(
                0,
                0,
                fruitCount,
                SpecialBombType.None
            );
            this.addScore(newScore, firstFruit, ScoreType.Score, 0, true);
        }

        dirty = this._objectsToDestroy.size > 0;

        this._objectsToDestroy.clear();
        return dirty;
    }

    startGame() {
        this._coroutineManager.stopCoroutine(this._runGameId);
        this._runGameId = this._coroutineManager.addCoroutine(
            new Coroutine(this._runGame())
        );
        Analytics.StartTimedEvent(AnalyticsEvent.ShowGame);
        // Statistics.GetInstance().AllowImmediateSave = false;
    }

    // private _runGameId = -1;
    // private *_runGame() {
    //     this.engine.log.info(CustomLogTag.Game, 'Running game');
    //     this.gameInProgress = false;
    //     this._puzzleState = -1;
    //     this._destroyBoard();

    //     this._score.resetScore();
    //     this._score.resetTime();

    //     this._gameOver = false;
    //     this.moveUpSoon = false;
    //     this.dropSpeedFactor = 1.0;
    //     this._level = 1;
    //     this._spawnNextPieceDelay = 0.0;

    //     this._randomVarianceFactor = Math.random();

    //     // We're starting a new game now.
    //     this._newGameDelegates.emit(this);

    //     yield null;

    //     this._spawner.startGame();
    //     this._rules.startNewGame();

    //     // opening UI
    //     this._score.startNewGame();

    //     this._moveBoardToPosition();

    //     this._stackChangeDelegates.emit(this);

    //     this.gameInProgress = true;

    //     yield* waitForSeconds(this.delayBeforeGameStart);

    //     this._puzzleState = 0;
    //     this._gameStartTime = this._time;
    // }

    getObject(row: number, column: number): BoardObject {
        const {validRow, validCol} = this.validateCell(row, column);
        if (validRow < 0 && validCol < 0) {
            this.engine.log.error(
                CustomLogTag.Game,
                `Invalid row and column sent to CellToBoardIndex (${row}, ${column})`
            );
            return null;
        }

        const index = this._cellToBoardIndex(validRow, validCol);
        let obj: BoardObject = null;
        if (index > -1) {
            obj = this._board[index];
        }
        return obj;
    }

    isRegisteredForActivation(row: number, col: number): boolean {
        for (let i = 0; i < this._pendingActivations.length; i++) {
            if (
                this._pendingActivations[i].Second.First == row &&
                this._pendingActivations[i].Second.Second == col
            ) {
                return true;
            }
        }
        return false;
    }

    registerForActivation(row: number, col: number) {
        const index = this._cellToBoardIndex(row, col);
        if (index > -1) {
            const obj = this._board[index];
            if (obj) {
                for (let i = 0; i < this._pendingActivations.length; i++) {
                    if (
                        this._pendingActivations[i].Second.First == row &&
                        this._pendingActivations[i].Second.Second == col
                    ) {
                        return;
                    }
                }
                const newPair = new Pair(row, col);
                this._pendingActivations.push(new Pair(obj, newPair));
            }
        }
    }

    addScore(
        newScore: number,
        blob: Object3D,
        spawnType: ScoreType,
        numBlobs: number,
        computeIsCombo: boolean
    ) {
        this._score.addScoreFx(newScore, blob, spawnType, numBlobs, computeIsCombo);
    }

    explode(row: number, col: number) {
        for (let i = 0; i < this._pendingChains.length; i++) {
            if (
                this._pendingChains[i].Second.First == row &&
                this._pendingChains[i].Second.Second == col
            ) {
                return;
            }
        }
        const newPair = new Pair(row, col);
        const obj = this._board[this._cellToBoardIndex(row, col)];
        this._pendingChains.push(new Pair(obj, newPair));
    }

    private _startChain(row: number, col: number) {
        const chainReactionVisited = new Map<BoardObject, number>();

        const index = this._cellToBoardIndex(row, col);
        const obj = this._board[index];

        if (obj == null) {
            return;
        }

        chainReactionVisited.set(obj, 0);

        this._chainReaction(row, col, 1, chainReactionVisited);

        let fruitCount = 0;
        let chainCount = 0;
        let loadedCount = 0;

        const blob = obj.object;

        // two passes: one to clear the board and the second to start the popping.
        for (let entry of chainReactionVisited) {
            const index = this._cellToBoardIndex(entry[0].row, entry[0].column);
            this._board[index] = null;
        }

        for (let entry of chainReactionVisited) {
            if (this._isFruit(entry[0])) {
                fruitCount++;
            } else {
                chainCount++;
                if (entry[0].loaded) {
                    loadedCount++;
                }
            }

            entry[0].pop(entry[1] * this.explosionStepDelay, entry[1], obj.explodeRainbow);
        }

        const newScore = this.rules.calculateScore(
            chainCount - loadedCount,
            loadedCount,
            fruitCount,
            SpecialBombType.None
        );

        this.addScore(newScore, blob, ScoreType.Score, chainCount + fruitCount, true);

        chainReactionVisited.clear();
    }

    /**
     *
     * @param row
     * @param col
     * @param step
     * @param chainReactionVisited The visited objects in the chain reaction. Object gets modified internally
     */
    private _chainReaction(
        row: number,
        col: number,
        step: number,
        chainReactionVisited: Map<BoardObject, number>
    ) {
        const index = this._cellToBoardIndex(row, col);
        const obj = this._board[index];

        // left
        const leftColumn = Mathf.repeat(col - 1, PuzzleConstants.columnCount);
        const leftIndex = this._cellToBoardIndex(row, leftColumn);
        const leftObj = this._board[leftIndex];

        if (this._exactColorMatch(obj, leftObj)) {
            const visited = chainReactionVisited.get(leftObj);
            if (visited === undefined || visited > step) {
                chainReactionVisited.set(leftObj, step);
                this._chainReaction(row, leftColumn, step + 1, chainReactionVisited);
            }
        }

        // right
        const rightColumn = Mathf.repeat(col + 1, PuzzleConstants.columnCount);
        const rightIndex = this._cellToBoardIndex(row, rightColumn);
        const rightObj = this._board[rightIndex];

        if (this._exactColorMatch(obj, rightObj)) {
            const visited = chainReactionVisited.get(rightObj);
            if (visited === undefined || visited > step) {
                chainReactionVisited.set(rightObj, step);
                this._chainReaction(row, rightColumn, step + 1, chainReactionVisited);
            }
        }

        // up
        if (row + 1 < PuzzleConstants.rowCount) {
            const topRow = row + 1;
            const topIndex = this._cellToBoardIndex(topRow, col);
            const topObj = this._board[topIndex];

            if (this._exactColorMatch(obj, topObj)) {
                const visited = chainReactionVisited.get(topObj);
                if (visited === undefined || visited > step) {
                    chainReactionVisited.set(topObj, step);
                    this._chainReaction(topRow, col, step + 1, chainReactionVisited);
                }
            }
        }

        // down
        if (row - 1 >= 0) {
            const bottomRow = row - 1;
            const bottomIndex = this._cellToBoardIndex(bottomRow, col);
            const bottomObj = this._board[bottomIndex];

            if (this._exactColorMatch(obj, bottomObj)) {
                const visited = chainReactionVisited.get(bottomObj);
                if (visited === undefined || visited > step) {
                    chainReactionVisited.set(bottomObj, step);
                    this._chainReaction(bottomRow, col, step + 1, chainReactionVisited);
                }
            }
        }
    }

    private _exactColorMatch(obj1: BoardObject, obj2: BoardObject): boolean {
        let match = false;
        if (obj1 && obj2) {
            match = obj1.matchesMyColor(obj2, true);
        }
        return match;
    }

    private _isFruit(obj: BoardObject): boolean {
        let fruit = false;
        if (obj) {
            fruit = obj.isFruit;
        }
        return fruit;
    }

    private _findCombinations(): boolean {
        let combined = false;
        for (let row = 0; row < PuzzleConstants.rowCount; row++) {
            for (let col = 0; col < PuzzleConstants.columnCount; col++) {
                let index = this._cellToBoardIndex(row, col);
                let obj = this._board[index];
                if (obj && obj.canBeAbsorbed()) {
                    if (this._tryToCombine(index, row, col)) {
                        combined = true;
                    }
                }
            }
        }
        return combined;
    }

    private _tryToCombine(index: number, row: number, col: number): boolean {
        let objectCombined = false;
        if (row > 0) {
            const obj = this._board[index];
            const indexBelow = this._cellToBoardIndex(row - 1, col);
            const belowObj = this._board[indexBelow];

            if (obj && belowObj && obj.canBeAbsorbed() !== belowObj.canBeAbsorbed()) {
                if (obj.canBeAbsorbed() && belowObj.canCombineWith(obj)) {
                    this._combine(index, indexBelow);
                    objectCombined = true;
                } else if (belowObj.canBeAbsorbed() && obj.canCombineWith(belowObj)) {
                    this._combine(indexBelow, index);
                    objectCombined = true;
                }
            }
        }
        return objectCombined;
    }

    private _combine(index: number, target: number) {
        const obj = this._board[index];
        const targetObj = this._board[target];

        this._board[index] = null;
        if (obj && targetObj) {
            targetObj.combineWith(obj);
        }
    }

    /**
     * Generates the columns around the main pilar of the game
     */
    private _generateColumns() {
        const rotationPerStep = (360 / PuzzleConstants.columnCount) * Mathf.Deg2Rad;

        this._lanes.length = 0;

        for (let x = 0; x < PuzzleConstants.columnCount; x++) {
            vec3.zero(tempVec);
            tempVec[0] = Math.sin(x * rotationPerStep) * PuzzleConstants.columnRadius;
            tempVec[2] = Math.cos(x * rotationPerStep) * PuzzleConstants.columnRadius;

            quat.identity(tempQuat);
            quat.fromEuler(
                tempQuat,
                0.0,
                PuzzleConstants.localColumnRotation + x * rotationPerStep * Mathf.Rad2Deg,
                0.0
            );

            const column = this.scene.addObject(this.object);
            column.setPositionLocal(tempVec);
            column.setRotationLocal(tempQuat);

            // use a collision component to easily visualize and debug the column
            // TODO: Remove this
            // column.addComponent(CollisionComponent, {
            //     collider: Collider.Box,
            //     extents: [0.5, 4, 0.5],
            // });

            this._lanes.push(column);
        }
    }

    addObject(obj: BoardObject, row: number, col: number) {
        const index = this._cellToBoardIndex(row, col);
        if (index >= 0 && index < this._board.length && !this._board[index]) {
            this._board[index] = obj;
            obj.object.parent = this._lanes[col];
            //obj.object.setRotationLocal(this._lanes[col].getRotationLocal());
            obj.object.resetPositionRotation();
            const pos = this._lanes[col].getPositionLocal([]);
            obj.object.setPositionLocal([0, pos[1] * PuzzleConstants.rowSize * row, 0]);

            //TODO: I'm not a fan of setting these properties from the outside,
            //should go through a method
            obj.row = row;
            obj.column = col;
            obj.camera = this._mainCamera;
            obj.puzzle = this;
        }
    }

    /**
     * adds an empty row to the bottom of the stack.
     */
    addRow() {
        // clear the top row
        // for (int x = 0; x < columnCount; x++)
        //     {
        //         if (board[x] != null)
        //         {
        //             // TODO: is this really correct?  In theory this case should never
        //             // happen--it's game over if there are items at the top of the board
        //             // when a row is added.  But in the case that it does occur, should
        //             // we just destroy the top row?
        //             Destroy(board[x].gameObject);
        //             board[x] = null;
        //         }
        //     }

        // shift everything up one slot.
        for (let row = PuzzleConstants.rowCount - 1; row > 0; row--) {
            const rowStartIndex = this._cellToBoardIndex(row, 0);
            const nextRowStartIndex = this._cellToBoardIndex(row - 1, 0);
            for (let col = 0; col < PuzzleConstants.columnCount; col++) {
                const index = rowStartIndex + col;
                const nextRowIndex = nextRowStartIndex + col;
                this._board[index] = this._board[nextRowIndex];
                this._board[nextRowIndex] = null;
            }
        }
    }

    offsetToRow(offset: number): number {
        let row = 0;
        if (offset > this.height) {
            row = -1;
        } else if (offset > 0.0) {
            const percent = offset / this.height;
            row = ~~(PuzzleConstants.rowCount * percent);
        }
        return row;
    }

    getRowOffset(row: number) {
        return row * PuzzleConstants.rowSize;
    }

    getHighestOccupiedRow(col: number): number {
        let highestRow = -1;
        for (let x = PuzzleConstants.rowCount - 1; x >= 0; x--) {
            const index = this._cellToBoardIndex(x, col);
            if (this._board[index] != null) {
                highestRow = x;
                break;
            }
        }
        return highestRow;
    }

    turnAdvance() {
        this._turnAdvance = true;
    }

    objectLanded() {
        this._fallingObjects--;
    }

    private _cellToBoardIndex(row: number, col: number) {
        let {validRow, validCol} = this.validateCell(row, col);
        if (validRow < 0 && validCol < 0) {
            this.engine.log.error(
                CustomLogTag.Game,
                /*Logger.User.McCool,*/ 'Invalid row and column sent to CellToBoardIndex (' +
                    row +
                    ', ' +
                    col +
                    ')'
            );
            return -1;
        }
        const realRow = this._bottomRow - validRow;
        const index = realRow * PuzzleConstants.columnCount + validCol;
        return index >= 0 && index < this._board.length ? index : -1;
    }

    /**
     *
     * @param row
     * @param col
     * @returns
     */
    validateCell(row: number, col: number) {
        let validRow = row;
        let validCol = col;
        if (row < 0 || row >= PuzzleConstants.rowCount) {
            validRow = -1;
            validCol = -1;
        } else {
            validCol = (col + PuzzleConstants.columnCount) % PuzzleConstants.columnCount;
        }
        return {validRow, validCol};
    }

    registerResetGameCallback(callback: (puzzle: Puzzle) => void) {
        this._resetGameDelegates.add(callback);
    }
    unregisterResetGameCallback(callback: (puzzle: Puzzle) => void) {
        this._resetGameDelegates.remove(callback);
    }
    registerNewGameCallback(callback: (puzzle: Puzzle) => void) {
        this._newGameDelegates.add(callback);
    }
    unregisterNewGameCallback(callback: (puzzle: Puzzle) => void) {
        this._newGameDelegates.remove(callback);
    }
    registerTurnCompleteCallback(callback: (puzzle: Puzzle) => void) {
        this._turnCompleteDelegates.add(callback);
    }
    unregisterTurnCompleteCallback(callback: (puzzle: Puzzle) => void) {
        this._turnCompleteDelegates.remove(callback);
    }
    registerGameOverCallback(callback: (puzzle: Puzzle) => void) {
        this._gameOverDelegates.add(callback);
    }
    unregisterGameOverCallback(callback: (puzzle: Puzzle) => void) {
        this._gameOverDelegates.remove(callback);
    }
    registerStackChangeCallback(callback: (puzzle: Puzzle) => void) {
        this._stackChangeDelegates.add(callback);
    }
    unregisterStackChangeCallback(callback: (puzzle: Puzzle) => void) {
        this._stackChangeDelegates.remove(callback);
    }

    registerForRemoval(row: number, col: number) {
        const index = this._cellToBoardIndex(row, col);
        if (index > -1) {
            const obj = this._board[index];
            if (obj) {
                for (let i = 0; i < this._pendingRemovals.length; i++) {
                    if (
                        this._pendingRemovals[i].Second.First == row &&
                        this._pendingRemovals[i].Second.Second == col
                    ) {
                        return;
                    }
                }
                const newPair = new Pair(row, col);
                this._pendingRemovals.push(new Pair(obj, newPair));
            }
        }
    }

    registerForPop(row: number, col: number, delay: number, distance: number) {
        const index = this._cellToBoardIndex(row, col);

        if (index > -1) {
            const obj = this._board[index];
            if (obj) {
                for (let i = 0; i < this._pendingPops.length; i++) {
                    if (
                        this._pendingPops[i].row == row &&
                        this._pendingPops[i].col == col
                    ) {
                        return;
                    }
                }
                this._pendingPops.push(new PopData(obj, row, col, delay, distance));
            }
        }
    }
    explodeRadial(row: number, col: number, radius: number) {
        // int x;
        // int y;
        // int fruitCount = 0;
        // int chainCount = 0;
        // int loadedCount = 0;
        // int startingLocation = CellToBoardIndex(row, col);
        // BoardObject thisObject = board[startingLocation];
        // Transform blob;
        // if (thisObject == null)
        // {
        // 	return;
        // }
        // else
        // {
        // 	blob = thisObject.transform;
        // }
        // Dictionary<BoardObject, float> chainReactionVisited = new Dictionary<BoardObject, float>();
        // // two passes so that board state is final when popping begins.
        // for (y = -radius; y <= radius; y++)
        // {
        // 	for (x = -radius; x <= radius; x++)
        // 	{
        // 		float distance = Mathf.Sqrt((x * x) + (y * y));
        // 		if (distance <= radius)
        // 		{
        // 			int validRow = -1;
        // 			int validCol = -1;
        // 			if (ValidateCell(row + x, col + y, out validRow, out validCol))
        // 			{
        // 				int index = CellToBoardIndex(validRow, validCol);
        // 				if (index >= 0 && index < board.Length && board[index])
        // 				{
        // 					BoardObject obj = board[index];
        // 					chainReactionVisited[obj] = distance;
        // 				}
        // 			}
        // 		}
        // 	}
        // }
        // foreach(KeyValuePair<BoardObject, float> entry in chainReactionVisited)
        // {
        // 	BoardObject obj = entry.Key;
        // 	if (IsFruit(obj))
        // 	{
        // 		fruitCount++;
        // 	}
        // 	else
        // 	{
        // 		chainCount++;
        // 		if (obj.Loaded)
        // 		{
        // 			loadedCount++;
        // 		}
        // 	}
        // 	float distance = entry.Value;
        // 	RegisterForPop(obj.Row, obj.Column, distance * explosionStepDelay, (int) distance);
        // }
        // chainReactionVisited.Clear();
        // int newScore = rules.CalculateScore(chainCount - loadedCount, loadedCount, fruitCount, SpecialBombType.CoconutBomb);
        // AddScore(newScore, blob, ScoreController.ScoreType.Score, chainCount + fruitCount, false);
    }
    explodeAllOfColor(row: number, col: number) {
        const index = this._cellToBoardIndex(row, col);
        let fruitCount = 0;
        let chainCount = 0;
        let loadedCount = 0;
        const obj = this._board[index];
        const chainReactionVisited = new Map<BoardObject, number>();
        // go through all rows and columns, again in two passes
        for (let x = 0; x < PuzzleConstants.rowCount; x++) {
            for (let y = 0; y < PuzzleConstants.columnCount; y++) {
                const compIndex = this._cellToBoardIndex(x, y);
                const compareObj = this._board[compIndex];
                // if you exist, compare to your colored blob
                if (compareObj) {
                    if (index == compIndex || this._exactColorMatch(obj, compareObj)) {
                        let step = chainCount + 1;
                        if (this._isFruit(compareObj)) {
                            fruitCount++;
                            step = fruitCount;
                        } else {
                            chainCount++;
                            if (compareObj.loaded) {
                                loadedCount++;
                            }
                        }
                        chainReactionVisited.set(compareObj, step);
                    }
                }
            }
        }
        for (let entry of chainReactionVisited) {
            const objToPop = entry[0];
            this.registerForPop(
                objToPop.row,
                objToPop.column,
                this.explodeAllOfColorDelay,
                entry[1]
            );
        }
        chainReactionVisited.clear();
        const newScore = this.rules.calculateScore(
            chainCount - loadedCount,
            loadedCount,
            fruitCount,
            SpecialBombType.CoconutBomb
        );
        this.addScore(
            newScore,
            obj.object,
            ScoreType.Score,
            chainCount + fruitCount,
            false
        );
    }

    objectCount(): number {
        let count = 0;

        for (let i = 0; i < this._board.length; i++) {
            if (this._board[i] != null) {
                count++;
            }
        }

        return count;
    }

    loadedObjectCount(): number {
        let count = 0;

        for (let i = 0; i < this._board.length; i++) {
            if (this._board[i] != null && this._board[i].loaded) {
                count++;
            }
        }

        return count;
    }

    findFirstLoadedObject(): {row: number; column: number} {
        let row = -1;
        let column = -1;
        for (let i = 0; i < this._board.length; i++) {
            if (this._board[i] != null && this._board[i].loaded) {
                return this._boardIndexToCell(i);
            }
        }
        return {row, column};
    }

    /**
     * Finds the first loaded object with fruit in the board.
     * @returns The row and column of the first loaded object with fruit.
     */
    findFirstLoadedObjectWithFruit(): {row: number; column: number} {
        let row = -1;
        let column = -1;
        for (let i = 0; i < this._board.length; i++) {
            if (this._board[i] != null && this._board[i].loaded) {
                if (Tags.hasTag(this._board[i].object, 'Fruit')) {
                    return this._boardIndexToCell(i);
                }
            }
        }
        return {row, column};
    }

    private _boardIndexToCell(index: number): {row: number; column: number} {
        const col = index % PuzzleConstants.columnCount;
        const cellRow = Math.floor(index / PuzzleConstants.columnCount);
        const row = this._bottomRow - cellRow;
        return {row, column: col};
    }

    private _destroyBoard() {
        for (let x = 0; x < this._board.length; x++) {
            if (this._board[x]) {
                this._board[x].landWhenDestroyed();
                this._board[x].object.destroy();
                this._board[x] = null;
            }
        }

        this._pendingActivations.length = 0;
        this._pendingPops.length = 0;
        this._pendingChains.length = 0;
        this._pendingRemovals.length = 0;
    }

    private _moveBoardToPosition() {
        for (let row = 0; row < PuzzleConstants.rowCount; row++) {
            let rowStartIndex = this._cellToBoardIndex(row, 0);
            for (let col = 0; col < PuzzleConstants.columnCount; col++) {
                const index = rowStartIndex + col;
                const obj = this._board[index];
                if (obj) {
                    obj.moveToPosition([0, PuzzleConstants.rowSize * row, 0]);
                    obj.row = row;
                }
            }
        }
    }

    resetGame() {
        if (this.gameInProgress) {
            this._puzzleState = 0;
            this._coroutineManager.stopCoroutine(this._runGameId);
            this._endGame();
        }

        this._destroyBoard();
        this._resetGameDelegates.emit(this);
    }

    private _endGame() {
        this.gameInProgress = false;
        this._turnCompleteDelegates.clear();
        this._stackChangeDelegates.clear();

        //TK: The original iterated backwards so that objects can remove themselves on reset.
        //Not sure how that should work here.
        this._gameOverDelegates.emit(this);

        Analytics.StopTimedEvent(AnalyticsEvent.ShowGame);
        //Statistics.GetInstance().AllowImmediateSave = true;
    }

    private _fallBack() {
        // TODO: we could optimize this by only visiting dirty columns.
        for (let col = 0; col < PuzzleConstants.columnCount; col++) {
            let nextEmptyRow = -1;
            let nextIsBottom = false;
            for (let row = 0; row < PuzzleConstants.rowCount; row++) {
                const index = this._cellToBoardIndex(row, col);
                if (this._board[index] == null) {
                    if (nextEmptyRow < 0) {
                        nextEmptyRow = row;
                    }
                    nextIsBottom = true;
                } else if (nextEmptyRow > -1 && this._board[index] != null) {
                    const obj = this._board[index];
                    this._board[index] = null;
                    const emptyIndex = this._cellToBoardIndex(nextEmptyRow, col);
                    this._board[emptyIndex] = obj;
                    if (obj.fallBack(nextEmptyRow, nextIsBottom)) {
                        this._fallingObjects++;
                    }
                    nextIsBottom = false;
                    nextEmptyRow++;
                }
            }
        }
    }
}
